import React, {
  createContext,
  useContext,
  useState,
  useEffect
} from 'react';
import { supabase, loginWithCredentials } from '../supabaseClient';
import {
  getStoredToken,
  getStoredUser,
  saveSession,
  clearSession,
  sanitizeUser
} from '../utils/session';
import { ensureOneSignalLogin, logoutOneSignal } from '../utils/oneSignalInit';
import {
  normalizePhone,
  parsePositiveInt,
  sanitizeText,
  validateEmail,
  validateOtpCode,
  validatePassword,
  validatePersonName,
  validateReferralCode,
  validateTransactionStatus
} from '../utils/inputValidation';
import { notifyTransactionChange } from '../utils/transactionNotifications';
import { syncNativePushSubscription } from '../utils/nativePush';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp debe ser usado dentro de AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resetPhone, setResetPhone] = useState(null);

  const applySession = async (sessionUser, token) => {
    const safeUser = sanitizeUser(sessionUser);
    saveSession(safeUser, token);
    setUser(safeUser);
    setIsAuthenticated(true);

    ensureOneSignalLogin(safeUser.id, { requirePermission: true }).catch((err) =>
      console.warn('OneSignal login:', err)
    );
    if (window.Notification?.permission === 'granted') {
      syncNativePushSubscription().catch((err) =>
        console.warn('Native push sync:', err)
      );
    }

    await fetchTransactions(safeUser.id);
    return safeUser;
  };

  const refreshUser = async () => {
    const token = getStoredToken();
    if (!token) return null;

    const { data, error } = await supabase.rpc('get_user_profile');
    if (error || !data) {
      clearSession();
      setUser(null);
      setIsAuthenticated(false);
      setTransactions([]);
      return null;
    }

    const profile = typeof data === 'string' ? JSON.parse(data) : data;
    saveSession(profile, token);
    setUser(profile);
    setIsAuthenticated(true);
    return profile;
  };

  useEffect(() => {
    const restoreSession = async () => {
      const token = getStoredToken();
      const savedUser = getStoredUser();

      if (!token || !savedUser) {
        setLoading(false);
        return;
      }

      setUser(savedUser);
      setIsAuthenticated(true);

      const profile = await refreshUser();
      if (profile) {
        await fetchTransactions(profile.id);
        ensureOneSignalLogin(profile.id, { requirePermission: true }).catch((err) =>
          console.warn('OneSignal login:', err)
        );
        if (window.Notification?.permission === 'granted') {
          syncNativePushSubscription().catch((err) =>
            console.warn('Native push sync:', err)
          );
        }
      }

      setLoading(false);
    };

    restoreSession();
  }, []);

  const login = async (identifier, password) => {
    const { user: loggedUser, access_token } = await loginWithCredentials(identifier, password);
    return applySession(loggedUser, access_token);
  };

  const register = async (userData, password) => {
    setLoading(true);
    try {
      const safePhone = normalizePhone(userData.phone);
      validatePassword(password);

      const { error } = await supabase.rpc('complete_registration', {
        p_phone: safePhone,
        p_first_name: validatePersonName(userData.first_name || '', 'Nombre'),
        p_last_name: validatePersonName(userData.last_name || '', 'Apellido'),
        p_email: validateEmail(userData.email),
        p_referral_code: validateReferralCode(userData.referral_code)
      });

      if (error) throw error;

      return login(safePhone, password);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    clearSession();
    setUser(null);
    setIsAuthenticated(false);
    setTransactions([]);

    logoutOneSignal();
  };

  const fetchTransactions = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        setTransactions([]);
        return;
      }

      const normalized = (data || []).map(t => ({
        ...t,
        amount: t.amount ?? 0,
        received_amount: t.received_amount ?? 0,
        created_at: t.created_at ?? new Date().toISOString(),
        status: t.status ?? 'Procesando',
        destination_reference_number: t.destination_reference_number ?? '',
        admin_reference_number: t.admin_reference_number ?? '',
        payment_method_origin: t.payment_method_origin ?? '',
        payment_method_destination: t.payment_method_destination ?? '',
        origin_country: t.origin_country ?? t.origin_country_code ?? '',
        destination_country: t.destination_country ?? t.destination_country_code ?? ''
      }));

      setTransactions(normalized);
    } catch {
      setTransactions([]);
    }
  };

  const addTransaction = async (transactionData) => {
    if (!user) return null;

    const { data, error } = await supabase
      .from('transactions')
      .insert({
        user_id: user.id,
        amount: transactionData.amount,
        received_amount: transactionData.receivedAmount,
        exchange_rate: transactionData.rate,
        origin_country_code: transactionData.originCountry,
        destination_country_code: transactionData.destinationCountry,
        payment_method_origin: transactionData.paymentMethodOrigin,
        payment_method_destination: transactionData.paymentMethodDestination,
        recipient_first_name: transactionData.recipient.firstName,
        recipient_last_name: transactionData.recipient.lastName,
        recipient_phone: transactionData.recipient.phone,
        recipient_email: transactionData.recipient.email,
        recipient_bank_name: transactionData.recipient.bankName,
        recipient_account_number: transactionData.recipient.accountNumber,
        recipient_id_number: transactionData.recipient.idNumber,
        payment_details_destination: transactionData.paymentDetailsDestination,
        client_reference_number: transactionData.clientReferenceNumber,
        status: 'Procesando'
      })
      .select();

    if (error) throw error;

    const tx = data[0];
    setTransactions(prev => [tx, ...prev]);

    try {
      await notifyTransactionChange(tx, {});
    } catch (err) {
      console.error('Error al notificar nueva remesa:', err);
    }

    return tx.id;
  };

  const updateTransactionStatus = async (transactionId, newStatus, reference = null) => {
    if (!user) throw new Error('No hay sesión activa.');

    const safeTransactionId = parsePositiveInt(transactionId, 'ID de transacción');
    const safeStatus = validateTransactionStatus(newStatus);
    const isAdmin = user.role?.toLowerCase().trim() === 'administrador';

    const { data: existingTx } = await supabase
      .from('transactions')
      .select('*')
      .eq('id', safeTransactionId)
      .maybeSingle();

    if (!existingTx) {
      throw new Error('No se encontró la transacción.');
    }

    const oldTransaction = existingTx;

    const updates = {
      status: safeStatus,
      updated_at: new Date().toISOString(),
    };

    if (reference !== null && reference !== undefined) {
      const safeReference = sanitizeText(reference, {
        fieldName: 'Referencia',
        maxLength: 120
      });
      if (safeStatus === 'Procesando') {
        updates.payment_reference = safeReference || null;
      } else {
        updates.destination_reference_number = safeReference || null;
      }
    }

    let updateQuery = supabase
      .from('transactions')
      .update(updates)
      .eq('id', safeTransactionId);

    if (!isAdmin) {
      updateQuery = updateQuery.eq('user_id', user.id);
    }

    const { data, error } = await updateQuery.select('*');

    if (error) {
      console.error('Error de Supabase:', error);
      throw error;
    }

    if (!data?.length) {
      throw new Error('No se encontró la transacción o no tienes permiso para actualizarla.');
    }

    const updatedTransaction = {
      ...data[0],
      user_id: data[0].user_id ?? oldTransaction.user_id,
    };

    setTransactions(prev =>
      prev.map(t => (t.id === safeTransactionId ? updatedTransaction : t))
    );

    try {
      await notifyTransactionChange(updatedTransaction, oldTransaction);
    } catch (err) {
      console.error('Error al disparar notificación OneSignal:', err);
    }

    return updatedTransaction;
  };

  const completeProfile = async (userId, firstName, lastName) => {
    setLoading(true);
    try {
      const safeUserId = parsePositiveInt(userId, 'ID de usuario');
      const safeFirstName = validatePersonName(firstName, 'Nombre');
      const safeLastName = validatePersonName(lastName, 'Apellido');

      const { data, error } = await supabase
        .from('users')
        .update({
          first_name: safeFirstName,
          last_name: safeLastName,
        })
        .eq('id', safeUserId)
        .select('id, phone, first_name, last_name, email, country, country_code, role, is_verified, referral_code, welcome_sent, created_at')
        .single();

      if (error) throw error;

      const token = getStoredToken();
      saveSession(data, token);
      setUser(data);
      return data;
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async (newPassword) => {
    if (!user) throw new Error('No hay sesión activa.');

    const safePassword = validatePassword(newPassword);

    const { error } = await supabase
      .from('users')
      .update({ password: safePassword })
      .eq('id', user.id);

    if (error) throw error;
  };

  const startPasswordReset = async (phone) => {
    const today = new Date().toDateString();
    const savedDate = localStorage.getItem('otp_date');
    let localAttempts = parseInt(localStorage.getItem('otp_attempts') || '0', 10);

    if (savedDate !== today) {
      localAttempts = 0;
      localStorage.setItem('otp_date', today);
    }

    if (localAttempts >= 3) {
      throw new Error('Límite de seguridad: Intenta de nuevo en 24 horas.');
    }

    const { error: otpError } = await supabase.rpc('request_password_reset_otp', {
      p_phone: normalizePhone(phone)
    });

    if (otpError) {
      if (otpError.message?.includes('Límite de seguridad') || otpError.code === 'P0001') {
        localStorage.setItem('otp_attempts', '3');
        throw new Error('Has superado el límite diario de códigos para este número.');
      }
      if (otpError.message?.includes('PHONE_NOT_REGISTERED')) {
        throw new Error('Este número no está registrado.');
      }
      if (otpError.code === '23505') {
        throw new Error('Ya enviamos un código de seguridad. Por favor, espera 15 minutos antes de solicitar uno nuevo.');
      }
      throw otpError;
    }

    localStorage.setItem('otp_attempts', (localAttempts + 1).toString());
    setResetPhone(phone);
    return true;
  };

  const verifyResetCode = async (phone, code) => {
    const { data, error } = await supabase.rpc('verify_password_reset_otp', {
      p_phone: normalizePhone(phone),
      p_code: validateOtpCode(code)
    });

    if (error) return false;

    const result = typeof data === 'string' ? JSON.parse(data) : data;
    if (!result?.ok) return false;

    setResetPhone(phone);
    return true;
  };

  const clearReset = async (phone) => {
    if (phone) {
      await supabase.rpc('cancel_password_reset', { p_phone: phone });
    }
    setResetPhone(null);
  };

  return (
    <AppContext.Provider
      value={{
        user,
        isAuthenticated,
        loading,
        transactions,
        login,
        register,
        logout,
        addTransaction,
        updateTransactionStatus,
        fetchTransactions,
        completeProfile,
        refreshUser,
        updatePassword,
        resetPhone,
        setResetPhone,
        startPasswordReset,
        verifyResetCode,
        clearReset
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
